import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell,
  ClipboardList,
  AlertTriangle,
  ClipboardCheck,
  Calendar,
  CheckCircle,
  Boxes,
  X,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react";
import { NotificationService } from "@veltro/shared";
import type { Notification, NotificationType } from "@veltro/shared/types";
import { getSupabase } from "@veltro/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";

// ── Severity colors: left border + unread dot ────────────────────────────────
type Severity = "urgent" | "action" | "success" | "warning" | "info";

const TYPE_SEVERITY: Record<NotificationType, Severity> = {
  work_order_assigned: "info",
  work_order_overdue: "urgent",
  inspection_due: "urgent",
  action_overdue: "urgent",
  approval_required: "action",
  approval_decision: "success",
  pm_due: "urgent",
  part_low_stock: "warning",
};

// Always-visible left border by severity
const SEVERITY_BORDER: Record<Severity, string> = {
  urgent:  "border-l-red-500",
  action:  "border-l-amber-500",
  success: "border-l-emerald-500",
  warning: "border-l-yellow-500",
  info:    "border-l-blue-500",
};

const SEVERITY_DOT: Record<Severity, string> = {
  urgent:  "bg-red-500",
  action:  "bg-amber-500",
  success: "bg-emerald-500",
  warning: "bg-yellow-500",
  info:    "bg-blue-500",
};

// Toast severity → Sonner variant mapping
const SEVERITY_TOAST: Record<Severity, "error" | "warning" | "success" | "info"> = {
  urgent:  "error",
  action:  "warning",
  success: "success",
  warning: "warning",
  info:    "info",
};

const TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  work_order_assigned: ClipboardList,
  work_order_overdue: AlertTriangle,
  inspection_due: ClipboardCheck,
  action_overdue: AlertTriangle,
  approval_required: CheckCircle,
  approval_decision: CheckCircle,
  pm_due: Calendar,
  part_low_stock: Boxes,
};

const ENTITY_ROUTES: Record<string, string> = {
  work_order: "/work-orders",
  inspection: "/inspections",
  action: "/actions",
  asset: "/assets",
  location: "/locations",
  pm_schedule: "/pm-schedules",
  checklist: "/checklists",
  template: "/templates",
  vendor: "/vendors",
  incident: "/incidents",
  meter_reading: "/meter-readings",
  escalation_rule: "/escalation-rules",
};

const MAX_NOTIFICATIONS = 50;

export function NotificationBell() {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Track known IDs to detect truly new notifications for toasts
  const knownIdsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    const [allResult, countResult] = await Promise.all([
      NotificationService.getAll({
        top: MAX_NOTIFICATIONS,
        orderBy: ["createdAt desc"],
      }),
      NotificationService.getUnreadCount(),
    ]);

    if (allResult.success && allResult.data) {
      setNotifications(allResult.data);

      // On initial load, seed known IDs without toasting
      if (initialLoadRef.current) {
        knownIdsRef.current = new Set(allResult.data.map((n) => n.id));
        initialLoadRef.current = false;
      }
    }
    if (countResult.success && countResult.data !== undefined) {
      setUnreadCount(countResult.data);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  // Realtime: listen for new notifications + show toast
  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) return;

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          const id = newRow.id as string;
          const title = (newRow.title as string) ?? "";
          const body = (newRow.body as string) ?? "";
          const type = (newRow.type as NotificationType) ?? "work_order_assigned";

          // Toast only for truly new notifications
          if (!knownIdsRef.current.has(id)) {
            knownIdsRef.current.add(id);
            const severity = TYPE_SEVERITY[type] ?? "info";
            const toastFn = toast[SEVERITY_TOAST[severity]];
            toastFn(title, { description: body });
          }

          void fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [fetchNotifications]);

  // Polling fallback: refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => {
      void fetchNotifications();
    }, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t("timeAgo.justNow");
    if (minutes < 60) return t("timeAgo.minutesAgo", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("timeAgo.hoursAgo", { count: hours });
    const days = Math.floor(hours / 24);
    return t("timeAgo.daysAgo", { count: days });
  }

  async function handleToggleRead(e: React.MouseEvent, notification: Notification) {
    e.stopPropagation();
    if (notification.read) {
      await NotificationService.markUnread(notification.id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, read: false, readAt: undefined } : n
        )
      );
      setUnreadCount((prev) => prev + 1);
    } else {
      await NotificationService.markRead(notification.id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id
            ? { ...n, read: true, readAt: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }

  async function handleDismiss(e: React.MouseEvent, notification: Notification) {
    e.stopPropagation();
    await NotificationService.delete(notification.id);
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (!notification.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    knownIdsRef.current.delete(notification.id);
  }

  async function handleMarkAllRead() {
    await NotificationService.markAllRead();
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true, readAt: n.readAt ?? new Date().toISOString() }))
    );
    setUnreadCount(0);
  }

  async function handleClearRead() {
    await NotificationService.deleteRead();
    setNotifications((prev) => {
      const remaining = prev.filter((n) => !n.read);
      // Clean up known IDs
      const removedIds = prev.filter((n) => n.read).map((n) => n.id);
      for (const id of removedIds) {
        knownIdsRef.current.delete(id);
      }
      return remaining;
    });
  }

  const readCount = notifications.filter((n) => n.read).length;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(true)}
        aria-label={t("title")}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
            {unreadCount}
          </span>
        )}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex flex-col p-0" showCloseButton={false}>
          <SheetHeader className="flex flex-row items-center justify-between border-b px-4 py-3">
            <SheetTitle>{t("title")}</SheetTitle>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => void handleMarkAllRead()}
                >
                  {t("markAllRead")}
                </Button>
              )}
              {readCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => void handleClearRead()}
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  {t("clearRead", "Clear read")}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                {t("noNotifications")}
              </div>
            ) : (
              <ul className="divide-y">
                {notifications.map((notification) => {
                  const Icon = TYPE_ICONS[notification.type] ?? Bell;
                  const severity = TYPE_SEVERITY[notification.type] ?? "info";
                  return (
                    <li
                      key={notification.id}
                      className={`group relative flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-muted/50 border-l-[3px] ${SEVERITY_BORDER[severity]} ${
                        !notification.read ? "bg-muted/30" : ""
                      }`}
                      onClick={() => {
                        if (!notification.read) {
                          void handleToggleRead(
                            { stopPropagation: () => {} } as React.MouseEvent,
                            { ...notification, read: false }
                          );
                        }
                        if (notification.entityType && notification.entityId) {
                          const basePath = ENTITY_ROUTES[notification.entityType];
                          if (basePath) {
                            setOpen(false);
                            navigate(`${basePath}/${notification.entityId}`);
                          }
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (!notification.read) {
                            void NotificationService.markRead(notification.id);
                            setNotifications((prev) =>
                              prev.map((n) =>
                                n.id === notification.id
                                  ? { ...n, read: true, readAt: new Date().toISOString() }
                                  : n
                              )
                            );
                            setUnreadCount((prev) => Math.max(0, prev - 1));
                          }
                          if (notification.entityType && notification.entityId) {
                            const basePath = ENTITY_ROUTES[notification.entityType];
                            if (basePath) {
                              setOpen(false);
                              navigate(`${basePath}/${notification.entityId}`);
                            }
                          }
                        }
                      }}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm leading-tight ${!notification.read ? "font-semibold" : "font-medium"}`}>
                          {notification.title}
                        </p>
                        <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
                          {notification.body}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {timeAgo(notification.createdAt)}
                        </p>
                      </div>
                      {/* Right column: fixed width, dot + hover actions overlay */}
                      <div className="relative mt-1 flex-shrink-0 w-5 h-5">
                        {/* Unread dot — always in flow */}
                        {!notification.read && (
                          <div className="absolute inset-0 flex items-start justify-center pt-0.5">
                            <div className={`h-2 w-2 rounded-full ${SEVERITY_DOT[severity]}`} />
                          </div>
                        )}
                        {/* Hover actions — absolute overlay, covers dot */}
                        <div className="absolute -right-1 -top-1 hidden group-hover:flex items-center gap-0.5 rounded-md bg-background shadow-sm border px-0.5 py-0.5">
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            onClick={(e) => void handleToggleRead(e, notification)}
                            title={notification.read ? t("markUnread", "Mark as unread") : t("markRead", "Mark as read")}
                          >
                            {notification.read ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                            onClick={(e) => void handleDismiss(e, notification)}
                            title={t("dismiss", "Dismiss")}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
